import {
  DeleteOutlined,
  DollarOutlined,
  DownloadOutlined,
  EditOutlined,
  LoadingOutlined,
  SettingOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Dropdown,
  message,
  Modal,
  Popconfirm,
  Popover,
  Select,
  Tooltip,
} from 'antd';
import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { downloadFile, fetchAPI } from '../../../utils/api';
import { formatUTCTimeToLocal } from '../../../utils/format';

import DownloadInvoice from '../DownloadInvoice';
import OrderGRNReminder from '../OrderGRNReminder';
import OrderInputTrackingNumber from '../OrderInputTrackingNumber';
import { CancelPOButton, GenerateDO, TrackingStatus } from '../OrderListing';
import OrderOperationStatusActions from '../OrderOperationStatusActions';
import OrderPaymentStatusTracking from '../OrderPaymentStatusTracking';
import OrderShipmentStatusTracking from '../OrderShipmentStatusTracking';
import SupplierPaymentCreateFromOrder from '../SupplierPaymentCreateFromOrder';
import { OrderV2 } from './types';
import OrderPartialPaymentRecords from '../OrderPartialPaymentRecords';
import OrderGenerateNcprSpid from '../OrderGenerateNcprSpid';
import OrderEscalationModal from './OrderEscalationModal';
import ModalAmazonOrderSync from '../../Settings/AdminActions/ModalAmazonOrderSync';

const ErPurRefNoSyncButton = ({
  record,
  refreshList,
}: {
  record: OrderV2;
  refreshList: () => void;
}) => {
  const [loading, setLoading] = useState(false);
  const sync = record?.email_reader_pur_ref_sync;

  if (!sync) {
    return null;
  }

  const syncedAt = sync.synced_at
    ? formatUTCTimeToLocal(sync.synced_at, 'YYYY/MM/DD')
    : null;

  const onSync = async () => {
    setLoading(true);
    try {
      const res = await fetchAPI(
        `orders/${record.id}/sync-email-reader-pur-ref-no`,
        { method: 'POST' }
      );
      if (res?.synced) {
        message.success('Pur ref no synced to 99sme');
      } else {
        message.error(res?.status || 'Failed to sync pur ref no to 99sme');
      }
      refreshList();
    } catch (error) {
      message.error('Failed to sync pur ref no to 99sme');
    }
    setLoading(false);
  };

  const button = (
    <Button
      icon={<SyncOutlined />}
      loading={loading}
      disabled={!!syncedAt}
      onClick={onSync}
    >
      Sync Pur Ref No
    </Button>
  );

  if (syncedAt) {
    return (
      <Popover
        content={`This PO already synced on ${syncedAt} — no need to resync`}
        placement="bottomLeft"
        trigger="hover"
      >
        <span>{button}</span>
      </Popover>
    );
  }

  return (
    <Tooltip title="Sync email-reader pur ref no to 99sme">{button}</Tooltip>
  );
};

const OrderActionColumn = (props: {
  user: any;
  record: OrderV2;
  refreshList: (options?: { silent: boolean }) => void;
  handleClickEditOrder: () => Promise<void>;
}) => {
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [operationsOpen, setOperationsOpen] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);
  const [showAmazonModal, setShowAmazonModal] = useState(false);
  const operationsOpenRef = useRef<boolean>(false);
  const financeOpenRef = useRef<boolean>(false);
  const [showPoPdfModal, setShowPoPdfModal] = useState(false);
  const [filterActiveOnly, setFilterActiveOnly] = useState(false);
  const [supplierPayments, setSupplierPayments] = useState<
    {
      id: number;
      supplier_name?: string;
      supplier?: { name?: string };
      region: string;
      total: number;
      currency: string;
    }[]
  >([]);
  const [selectedSpId, setSelectedSpId] = useState<number | null>(null);

  const operationActions = (
    <ActionMenu $twoColumn onClick={() => setOperationsOpen(false)}>
      <OrderOperationStatusActions
        record={props.record}
        user={props.user}
        refreshList={() => {
          setOperationsOpen(false);
          props.refreshList();
        }}
      />
      <CancelPOButton
        record={props.record}
        onOpenChange={() => {
          setOperationsOpen(false);
        }}
        refreshList={() => {
          setOperationsOpen(false);
          props.refreshList();
        }}
      />
      {props.user.role === 'superadmin' && (
        <Popconfirm
          title="Are you sure to delete this Order Item?"
          onConfirm={async () => {
            setOperationsOpen(false);
            const hide = message.loading('Deleting...');
            await fetchAPI(`order/delete`, {
              payload: { id: props.record.id },
              method: 'POST',
            });
            hide();
            props.refreshList();
          }}
          onCancel={() => {}}
          okText="Yes"
          cancelText="No"
        >
          <Button danger icon={<DeleteOutlined />}>
            Delete
          </Button>
        </Popconfirm>
      )}
      <GenerateDO
        record={props.record}
        onOpenChange={() => {
          setOperationsOpen(false);
        }}
      />
      <TrackingStatus
        record={props.record}
        onOpenChange={() => {
          setOperationsOpen(false);
        }}
      />
      <OrderShipmentStatusTracking
        record={props.record}
        onOpenChange={() => {
          setOperationsOpen(false);
        }}
      />
      <OrderInputTrackingNumber
        record={props.record}
        onSuccess={() => {
          setOperationsOpen(false);
          props.refreshList();
        }}
      />
      <SupplierPaymentCreateFromOrder
        record={props.record}
        onOpenChange={() => {
          setOperationsOpen(false);
        }}
        reloadOrderList={() => {
          setOperationsOpen(false);
          props.refreshList();
        }}
      />
      <OrderGRNReminder
        record={props.record}
        onOpenChange={() => {
          setOperationsOpen(false);
        }}
        onSuccess={() => {
          setOperationsOpen(false);
          props.refreshList();
        }}
      />
      <OrderGenerateNcprSpid
        orderId={props.record?.id}
        reloadOrderList={() => {
          setOperationsOpen(false);
          props.refreshList();
        }}
        onOpenChange={() => {
          setOperationsOpen(false);
        }}
      />
      <OrderEscalationModal
        record={props.record}
        onOpenChange={() => {
          setOperationsOpen(false);
        }}
        onSuccess={() => {
          setOperationsOpen(false);
          props.refreshList();
        }}
      />
      <ErPurRefNoSyncButton
        record={props.record}
        refreshList={() => {
          setOperationsOpen(false);
          props.refreshList();
        }}
      />
      <Button
        icon={<SyncOutlined />}
        onClick={() => {
          setOperationsOpen(false);
          setShowAmazonModal(true);
        }}
      >
        Amazon PO (V1)
      </Button>
      <Button
        icon={<DownloadOutlined />}
        onClick={async () => {
          setOperationsOpen(false);
          const hide = message.loading('Generating DO PDF...');
          try {
            const blob = await fetchAPI(
              `orders/gen-do/pdf/${props.record.id}`,
              {
                method: 'GET',
              }
            );
            downloadFile(blob, `DO_${props.record.id}.pdf`);
            hide();
            message.success('DO PDF generated successfully');
          } catch (e) {
            hide();
            message.error('Failed to generate DO PDF');
          }
        }}
      >
        Generate DO (PDF)
      </Button>
      <Button
        icon={<DownloadOutlined />}
        onClick={async () => {
          setOperationsOpen(false);
          setShowPoPdfModal(true);
          setFilterActiveOnly(false);
          setSelectedSpId(null);
          try {
            const res = await fetchAPI('supplier-payments/all', {
              method: 'POST',
              payload: { sa_id: props.record.id, per_page: 50 },
            });
            setSupplierPayments(res?.items ?? []);
          } catch {
            setSupplierPayments([]);
          }
        }}
      >
        Generate PO (PDF)
      </Button>
    </ActionMenu>
  );

  const financeActions = (
    <ActionMenu onClick={() => setFinanceOpen(false)}>
      <DownloadInvoice
        record={props.record}
        onOpenChange={() => {
          setFinanceOpen(false);
        }}
      />
      <OrderPaymentStatusTracking
        record={props.record}
        onOpenChange={() => {
          setFinanceOpen(false);
        }}
      />
      <OrderPartialPaymentRecords
        record={props.record}
        onOpenChange={() => {
          setFinanceOpen(false);
        }}
      />
    </ActionMenu>
  );

  return (
    <StyledDiv>
      <EditButton
        icon={loadingEdit ? <LoadingOutlined /> : <EditOutlined />}
        onClick={async () => {
          setLoadingEdit(true);
          await props.handleClickEditOrder();
          setLoadingEdit(false);
        }}
      >
        Edit
      </EditButton>
      <Dropdown
        visible={operationsOpen}
        onVisibleChange={visible => {
          // Allow open/close normally, but prevent override when button was clicked
          if (!operationsOpenRef.current) {
            setOperationsOpen(visible);
          }
          operationsOpenRef.current = false;
        }}
        overlay={operationActions}
        trigger={['click']}
        placement="bottomLeft"
      >
        <OperationsButton icon={<SettingOutlined />}>
          Operations
        </OperationsButton>
      </Dropdown>
      <Dropdown
        visible={financeOpen}
        onVisibleChange={visible => {
          if (!financeOpenRef.current) {
            setFinanceOpen(visible);
          }
          financeOpenRef.current = false;
        }}
        overlay={financeActions}
        trigger={['click']}
        placement="bottomLeft"
      >
        <FinanceButton icon={<DollarOutlined />}>Finance</FinanceButton>
      </Dropdown>
      <ModalAmazonOrderSync
        visible={showAmazonModal}
        onCancel={() => setShowAmazonModal(false)}
        orderId={props.record.id}
        onSuccess={() => props.refreshList()}
      />
      <Modal
        title="Generate PO PDF"
        visible={showPoPdfModal}
        onCancel={() => {
          setShowPoPdfModal(false);
          setFilterActiveOnly(false);
        }}
        onOk={async () => {
          setShowPoPdfModal(false);
          if (!selectedSpId) return;
          const hide = message.loading('Generating PO PDF...');
          try {
            const blob = await fetchAPI('orders/gen-po/pdf', {
              method: 'POST',
              payload: {
                order_id: props.record.id,
                supplier_payment_id: selectedSpId,
                ...(filterActiveOnly ? { filter_active_only: true } : {}),
              },
            });
            downloadFile(blob, `PO_${props.record.id}_${selectedSpId}.pdf`);
            hide();
            message.success('PO PDF generated successfully');
          } catch (e) {
            hide();
            message.error('Failed to generate PO PDF');
          }
          setFilterActiveOnly(false);
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 500, display: 'block', marginBottom: 4 }}>
            Select Supplier Payment
          </label>
          <Select
            style={{ width: '100%' }}
            placeholder="Search and select SPID"
            showSearch
            value={selectedSpId}
            onChange={val => setSelectedSpId(val as number)}
            filterOption={(input, option) =>
              String(option?.label ?? '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            options={supplierPayments.map(sp => ({
              value: sp.id,
              label: `#${sp.id} | ${sp.supplier_name ??
                sp.supplier?.name ??
                '-'} | ${sp.region} | ${sp.currency} ${Number(
                sp.total
              ).toLocaleString()}`,
            }))}
          />
        </div>
        <Checkbox
          checked={filterActiveOnly}
          onChange={e => setFilterActiveOnly(e.target.checked)}
        >
          Only include items with active status
        </Checkbox>
      </Modal>
    </StyledDiv>
  );
};

const StyledDiv = styled.div`
  min-width: 120px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px;

  button {
    width: 100%;
    text-align: left;
  }
`;

const OperationsButton = styled(Button)`
  border-color: #1890ff;
  color: #1890ff;

  &:hover {
    border-color: #40a9ff;
    color: #40a9ff;
  }
`;

const EditButton = styled(Button)`
  border-color: #722ed1;
  color: #722ed1;

  &:hover {
    border-color: #9254de;
    color: #9254de;
  }
`;

const FinanceButton = styled(Button)`
  border-color: #52c41a;
  color: #52c41a;

  &:hover {
    border-color: #73d13d;
    color: #73d13d;
  }
`;

const ActionMenu = styled.div<{ $twoColumn?: boolean }>`
  display: grid;
  grid-template-columns: ${({ $twoColumn }) =>
    $twoColumn ? '1fr 1fr' : '1fr'};
  gap: 4px;
  padding: 8px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: ${({ $twoColumn }) => ($twoColumn ? '420px' : '200px')};

  button {
    width: 100%;
    text-align: left;
    justify-content: flex-start;
  }
`;

export default OrderActionColumn;
